import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import LoadingModal from 'src/components/navigation/LoadingModal';
import { badgeCardTypeOptions } from 'src/features/cards/utils/constants';
import { useListVisitsStatistics } from 'src/features/statistics/api/useListVisitsStatistics';
import { type VisitsStatistic, VISITS_EXPORT_MESSAGES_MAP, VISITS_EXPORT_PROPS } from 'src/features/statistics/types';
import { statisticsMessages } from 'src/features/statistics/utils';
import { Button } from 'src/registry/ui/Button';
import { formatTime } from 'src/utils/formatTime';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { generalMessages } from 'src/utils/messages';

type Props = {
    parkingId: string;
    dateTime: {
        beforeDate: Date;
        afterDate: Date;
    };
    querySearchOptions: { [p: string]: string };
    querySortingOptions: { ordering: string | undefined };
    queryFiltersOptions: { [p: string]: any } | any[];
    disabled: boolean;
};

const LIMIT = 100;

export default function ParkingVisitsExport({
    parkingId,
    dateTime,
    querySortingOptions,
    queryFiltersOptions,
    querySearchOptions,
    disabled,
}: Props) {
    const [offset, setOffset] = React.useState<null | number>(null);
    const [exportJSON, setExportJSON] = React.useState<VisitsStatistic[]>([]);
    const { formatMessage } = useIntl();

    const enabled = React.useMemo(() => typeof offset === 'number', [offset]);

    const { data: exportDetails } = useListVisitsStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        offset: offset || 0,
        limit: LIMIT,
        config: { enabled },
    });

    const onExportEnd = React.useCallback(() => {
        setExportJSON([]);
        setOffset(null);
    }, []);

    const json2Csv = (jsonData: VisitsStatistic[]) => {
        const keys = Object.values(VISITS_EXPORT_PROPS) as VISITS_EXPORT_PROPS[];
        const csvRows = [];

        const dateTime = [
            VISITS_EXPORT_PROPS.EXIT_DATETIME,
            VISITS_EXPORT_PROPS.ENTER_DATETIME,
            VISITS_EXPORT_PROPS.PRICE_PAID_TIME,
        ];
        const hours = [VISITS_EXPORT_PROPS.EXIT_HOUR, VISITS_EXPORT_PROPS.ENTER_HOUR, VISITS_EXPORT_PROPS.PAID_HOUR];

        csvRows.push(keys.map((key) => formatMessage(VISITS_EXPORT_MESSAGES_MAP[key])).join(','));
        jsonData.forEach((row) => {
            const values = keys
                .filter((key) => !hours.includes(key))
                .map((key) => {
                    const rawValue = (row as Record<string, any>)[key] as string | null | undefined;
                    let value = rawValue === null || rawValue === undefined ? '' : rawValue.toString();
                    if (dateTime.includes(key)) {
                        const datetime = new Date(value);
                        const formattedDate = new Date(datetime.toString()).toLocaleDateString('bg-BG');
                        const formattedTime = formatTime(datetime);
                        return `${formattedDate},${formattedTime}`;
                    }

                    if (key === VISITS_EXPORT_PROPS.CARD_TYPE) value = formatMessage(badgeCardTypeOptions[value].label);

                    switch (key) {
                        case VISITS_EXPORT_PROPS.CURRENCY:
                            return value ?? 'лв.';
                        case VISITS_EXPORT_PROPS.PRICE_PAID:
                            return Number(value) / 100;
                    }

                    if (value.includes(',')) value = `"${value}"`;

                    return value;
                });
            csvRows.push(values.join(','));
        });

        return csvRows.join('\n');
    };

    const generateCSV = (data: VisitsStatistic[], title: string) => {
        const csv = json2Csv(data);
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const { formattedDate } = getFormattedDateTime(new Date().toISOString());
        link.href = url;
        link.download = `${title}_${formattedDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    React.useEffect(() => {
        if (!enabled || !exportDetails || !exportDetails.results) return;
        if (exportDetails.count > (offset || 0) + LIMIT) {
            setExportJSON((prevState) => prevState.concat(exportDetails.results));
            return setOffset((prevState) => (prevState || 0) + LIMIT);
        }
        generateCSV(exportJSON.concat(exportDetails.results), 'Parking visits');
        onExportEnd();
    }, [setExportJSON, offset, generateCSV, exportDetails]);

    return (
        <>
            <LoadingModal
                isLoading={enabled}
                title={
                    <FormattedMessage
                        id={statisticsMessages.exportingVisits.id}
                        defaultMessage={statisticsMessages.exportingVisits.defaultMessage}
                    />
                }
                onCancel={onExportEnd}
            />
            <Button onClick={() => setOffset(0)} disabled={disabled}>
                <FormattedMessage {...generalMessages.exportCSV} />
            </Button>
        </>
    );
}
